import { faSearch, faArrowUpAZ, faArrowUpZA, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import cn from 'classnames';
import { useSelectable } from 'hooks/useSelectable';
import useDebounce from 'hooks/useDebounce';
import { getGroups, deleteGroups } from '@redux/features/groupsSlice';
import SessionStorageUtils from 'utils/SessionStorageUtils';
import { SessionStorageKeys } from 'constants/sessionStorageKeys';
import { SelectionInfo } from 'components/SelectionInfo';
import { NoResults } from 'components/NoResults';
import { Checkbox } from 'components/Checkbox';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { Input } from 'components/Input';
import { CreateGroup } from './CreateGroup';
import { GroupCard } from './GroupCard';
import { PageSelector } from '../PageSelector';
import mainStyles from 'pages/Database/Database.scss';
import styles from './Groups.scss';

export const Groups = () => {
  const currentLocation = SessionStorageUtils.getData(SessionStorageKeys.LOCATION);
  const { selectedIDs, toggleSelection, clearSelection } = useSelectable();
  const { groups, isLoading } = useSelector((state) => state.groups);
  const { locations } = useSelector((state) => state.locations);
  const [showOnlyClubGroups, setShowOnlyClubGroups] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const dispatch = useDispatch();

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    dispatch(
      getGroups({
        search: debouncedSearch,
        ...(currentLocation && { locationIDs: currentLocation.id }),
      })
    );
  }, [debouncedSearch]);

  const handleDelete = async () => {
    if (selectedIDs.length > 0) {
      const result = await dispatch(deleteGroups(selectedIDs));

      if (result.meta.requestStatus === 'fulfilled') {
        toggleSelectMode();
      }
    }
  };

  const sortedGroups = [...groups].sort((a, b) => {
    const nameCompare = a.name.localeCompare(b.name);
    return sortAsc ? nameCompare : -nameCompare;
  });

  const filteredGroups = showOnlyClubGroups ? sortedGroups.filter((group) => group.isClub) : sortedGroups;

  const toggleSelectMode = () => {
    setIsSelectionMode(!isSelectionMode);

    if (isSelectionMode) {
      clearSelection();
    }
  };

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h1>
            <FontAwesomeIcon icon={faUsers} />
            База даних
          </h1>
          <p>Огляд клієнтів, тренерів та груп</p>
        </div>
        <PageSelector />
        <div className={styles.buttons}>
          <SelectionInfo
            selectedItems={selectedIDs}
            wordForms={['групу', 'групи', 'груп']}
            isSelectionMode={isSelectionMode}
          />
          <Button
            text={isSelectionMode ? 'Відміна' : 'Виділити'}
            onClick={toggleSelectMode}
            ghostStyle={isSelectionMode}
            smallStyle
          />
          <Button text='Видалити' onClick={handleDelete} disabled={selectedIDs.length === 0} smallStyle />
        </div>
      </div>
      <div className={styles.actions}>
        <Input
          placeholder='Назва групи'
          icon={faSearch}
          value={searchQuery}
          onChange={({ target }) => setSearchQuery(target.value)}
        />
        <button className={styles.sortBtn} onClick={() => setSortAsc(!sortAsc)}>
          <FontAwesomeIcon icon={sortAsc ? faArrowUpAZ : faArrowUpZA} />
        </button>
        <Checkbox
          label='Групи клубу'
          checked={showOnlyClubGroups}
          onChange={() => setShowOnlyClubGroups(!showOnlyClubGroups)}
        />
        <Button className={styles.createBtn} text='Створити' onClick={() => setIsCreateModalOpen(true)} smallStyle />
        <Modal isOpen={isCreateModalOpen} setIsOpen={setIsCreateModalOpen}>
          <CreateGroup setIsModalOpen={setIsCreateModalOpen} />
        </Modal>
      </div>
      {filteredGroups.length > 0 ? (
        <div className={cn(mainStyles.table, styles.grid)}>
          {filteredGroups.map((group) => (
            <GroupCard
              group={group}
              location={locations.find(({ id }) => id === group.locationID)}
              isSelected={selectedIDs.includes(group.id)}
              toggleSelection={toggleSelection}
              isSelectionMode={isSelectionMode}
              isShowLocation={currentLocation.admin}
              key={group.id}
            />
          ))}
        </div>
      ) : (
        !isLoading && <NoResults />
      )}
    </>
  );
};
